/**
 * Demo Data Utilities
 *
 * Generates realistic mock data for demo site displays.
 * Uses deterministic hashing so the same unit always gets the same values.
 */

/**
 * Simple hash function for deterministic values from string
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Generate deterministic demo SOC value based on unit ID
 * Returns a value between 25-95% (realistic battery range)
 */
export function getDemoSoc(unitId: string): number {
  const hash = hashString(unitId);
  // Generate SOC between 25-95% (avoids unrealistic edge cases)
  return 25 + (hash % 71);
}

/**
 * Generate deterministic demo generation value (kW) based on unit ID
 * Returns a value between 1.5-8.5 kW for ~65% of units (realistic daytime solar)
 * Returns 0 for ~35% of units (simulating units not receiving sun)
 */
export function getDemoGeneration(unitId: string): number {
  const hash = hashString(unitId + '_gen');

  // ~65% of units should be generating (realistic daytime)
  if ((hash % 100) >= 65) return 0;

  // Generate between 1.5-8.5 kW
  return Math.round((1.5 + (hash % 70) / 10) * 10) / 10;
}

/**
 * Generate deterministic demo consumption value (kW) based on unit ID
 * Returns a value between 0.5-4.5 kW (typical load range)
 */
export function getDemoConsumption(unitId: string): number {
  const hash = hashString(unitId + '_load');
  // Generate between 0.5-4.5 kW
  return Math.round((0.5 + (hash % 40) / 10) * 10) / 10;
}

/**
 * Generate deterministic demo charger power output (kW) based on unit ID
 * ~30% of units are actively charging (3-11 kW), rest are idle (0 kW)
 */
export function getDemoChargerPower(unitId: string): number {
  const hash = hashString(unitId + '_charger');
  // Only ~30% of units should be actively charging
  if ((hash % 100) >= 30) return 0;
  // Generate between 3-11 kW (typical Tesla Wall Connector range)
  return Math.round((3 + (hash % 80) / 10) * 10) / 10;
}

/**
 * Generate deterministic demo HVAC active state based on unit ID
 * ~45% of units have HVAC running (realistic for mixed fleet)
 */
export function getDemoHvacActive(unitId: string): boolean {
  const hash = hashString(unitId + '_hvac');
  return (hash % 100) < 45;
}

/**
 * Generate deterministic demo HVAC mode based on unit ID
 * Returns 'cool', 'heat', or 'fan' for active units
 */
export function getDemoHvacMode(unitId: string): 'cool' | 'heat' | 'fan' | 'off' {
  if (!getDemoHvacActive(unitId)) return 'off';
  const hash = hashString(unitId + '_hvac_mode');
  const modes: ('cool' | 'heat' | 'fan')[] = ['cool', 'cool', 'cool', 'heat', 'fan']; // Weighted toward cool
  return modes[hash % modes.length];
}
